<script setup>
import { VForm } from 'vuetify/components/VForm'
import { themeConfig } from '@themeConfig'
import { useTrainerAccountStore } from '@/stores/trainerAccount'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { safeTrainerRedirectPath } from '@/utils/safeRedirect'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const accountStore = useTrainerAccountStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const isPasswordVisible = ref(false)
const formError = ref('')
const refVForm = ref()

const credentials = ref({
  email: '',
  password: '',
})

const login = async () => {
  formError.value = ''
  try {
    await accountStore.login(credentials.value)
    await router.replace(safeTrainerRedirectPath(route.query.to, accountStore.postLoginRedirectPath()))
  }
  catch (err) {
    formError.value = err.payload?.error || err.message || t('login.genericError')
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid }) => {
    if (valid)
      login()
  })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="480"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <RouterLink
          to="/"
          class="d-flex align-center gap-x-3 mb-6 text-decoration-none"
        >
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="text-h5 text-high-emphasis">FitX</span>
        </RouterLink>

        <h1 class="text-h4 mb-1">
          {{ $t('login.title') }}
        </h1>
        <p class="mb-0 text-medium-emphasis">
          {{ $t('login.subtitle') }}
        </p>
      </VCardText>

      <VCardText>
        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VForm
          ref="refVForm"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="credentials.email"
                :label="$t('common.email')"
                type="email"
                autocomplete="username"
                autofocus
                :rules="[requiredValidator, emailValidator]"
                :disabled="accountStore.loading"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="credentials.password"
                :label="$t('common.password')"
                autocomplete="current-password"
                :rules="[requiredValidator]"
                :type="isPasswordVisible ? 'text' : 'password'"
                :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                :disabled="accountStore.loading"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />

              <div class="d-flex justify-end my-4">
                <RouterLink
                  class="text-primary"
                  :to="{ name: 'forgot-password' }"
                >
                  {{ $t('login.forgotPassword') }}
                </RouterLink>
              </div>

              <VBtn
                block
                type="submit"
                :loading="accountStore.loading"
              >
                {{ $t('login.submit') }}
              </VBtn>
            </VCol>

            <VCol
              cols="12"
              class="text-center"
            >
              <span class="text-medium-emphasis">{{ $t('login.newTrainer') }}</span>
              <RouterLink
                class="text-primary ms-1"
                :to="{ name: 'register' }"
              >
                {{ $t('login.createAccount') }}
              </RouterLink>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
